import React from 'react';
import { Tooltip, Typography, theme } from 'antd';
import type { WorkerDaySeries } from '../utils/workerTelemetry';

const { Text } = Typography;

// A bar per day, tallest = busiest. Enough to see a worker that stopped being
// used, or one that only works office hours.
//
// The bars are sized to the column, not the other way round: the window can be
// 1 day or 60, and a fixed 6px bar turned "Last 60 days" into 400px of table
// nobody could reach on a 1280px screen.
//
// One axis for the whole table. `max` is the busiest day of ANY worker, passed
// in by the page, so bar heights mean the same thing on every row. Scaling each
// row to its own peak made a worker doing 12 checks a day and one doing 12,000
// draw the identical picture — the single most misleading thing a sparkline
// can do. Without `max` it falls back to its own peak, which is only correct
// when the chart stands alone.
interface Props {
  days: WorkerDaySeries[];
  /** Space the bars must fit into, in CSS px. */
  width?: number;
  height?: number;
  pick?: (d: WorkerDaySeries) => number;
  /** Shared ceiling for the y axis; omit only for a stand-alone sparkline. */
  max?: number;
  /** Named once, in the tooltip — the bars carry no axis labels. */
  unit?: string;
}

const WorkerSparkline: React.FC<Props> = ({
  days, width = 64, height = 20, pick, max, unit = 'checks',
}) => {
  const { token } = theme.useToken();
  const value = pick || ((d: WorkerDaySeries) => d.checks);
  if (!days.length) return <Text type="secondary">—</Text>;

  const values = days.map(value);
  const ceiling = Math.max(1, max ?? Math.max(...values));
  // A 1px gap in the surface colour is what separates touching bars; below
  // ~30 days there is room for it, above that the bars are hairlines already.
  const gap = days.length > 30 ? 0 : 1;
  const bar = Math.max(1, Math.min(8, Math.floor((width - gap * (days.length - 1)) / days.length)));

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap,
        height: height + 1,
        // Hairline baseline: without it a row of near-zero days is
        // indistinguishable from an empty cell.
        borderBottom: `1px solid ${token.colorSplit}`,
        width: 'fit-content',
      }}
    >
      {days.map((d) => {
        const v = value(d);
        return (
          <Tooltip key={d.day} title={`${d.day} — ${v.toLocaleString()} ${unit}`}>
            <div
              style={{
                width: bar,
                flex: 'none',
                // A day with no checks keeps a visible stub on the baseline, in
                // the muted fill: "nothing happened" is a reading, not a gap.
                height: v > 0 ? Math.max(2, Math.round((v / ceiling) * height)) : 1,
                background: v > 0 ? token.colorPrimary : token.colorFillSecondary,
                borderRadius: '2px 2px 0 0',
              }}
            />
          </Tooltip>
        );
      })}
    </div>
  );
};

export default WorkerSparkline;
